// Junction turning-matrix Excel export with three scenario tabs:
//   Existing  ·  Proposed  ·  Existing + Proposed
// Matches the schematic editor's export look — per-junction diagram image in
// columns A–G, a bordered/shaded matrix from column H (START_COL), a
// "Junction: X" title, and a divider between junctions — but organised into the
// three cases. All three sheets are laid out IDENTICALLY so the combined sheet
// references the other two with live formulas (edit an Existing figure and the
// combined recalculates). Row/column totals are SUM formulas.

const START_COL = 8;   // leave columns A–G for the 300px diagram image
const BLOCK_ROWS = 24; // rows one junction block occupies when stacked

function colLetter(n) {
  let s = '';
  while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); }
  return s;
}

// Build the workbook (separate from the browser download so it can be
// unit-tested in Node). `cellValue(junction, fromArm, toArm, ownAddr)` returns
// a number, string, or an exceljs formula object for a data cell.
// Excel-safe worksheet name: ≤31 chars, none of : \ / ? * [ ], and unique.
function sheetName(raw, used) {
  let base = String(raw || 'Sheet').replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 31) || 'Sheet';
  let name = base, n = 2;
  while (used.has(name)) { const suffix = ' ' + n++; name = base.slice(0, 31 - suffix.length) + suffix; }
  used.add(name);
  return name;
}

export function buildJunctionWorkbook(ExcelJS, { junctionMatrices, existingFlows = {}, periodLabel = '', projectName = 'project', flowScenarios = [], growthMultById = {} }) {
  const junctions = junctionMatrices || [];
  const wb = new ExcelJS.Workbook();

  const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
  const headerFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F0FA' } };
  const centered = { horizontal: 'center', vertical: 'middle' };

  const exAt = (label, from, to) => (existingFlows[label] && existingFlows[label][from] && existingFlows[label][from][to]) || 0;
  const devAt = (j, from, to) => (j.matrix && j.matrix[from] && j.matrix[from][to]) || 0;

  function layoutSheet(ws, cellValue) {
    ws.getCell(1, START_COL).value = `${projectName} — junction turning matrices${periodLabel ? ' (' + periodLabel + ')' : ''}`;
    ws.getCell(1, START_COL).font = { bold: true, size: 14 };

    let idx = 0;
    for (const j of junctions) {
      const arms = j.armNames || [];
      const n = arms.length;
      const startRow = 3 + idx * BLOCK_ROWS;

      // Per-junction diagram image (cols A–G), if we have one.
      if (j.diagramPng) {
        try {
          const imageId = wb.addImage({ base64: String(j.diagramPng).replace(/^data:image\/png;base64,/, ''), extension: 'png' });
          ws.addImage(imageId, { tl: { col: 0, row: startRow }, ext: { width: 300, height: 300 } });
        } catch (e) { /* skip image on error */ }
      }

      ws.getCell(startRow, START_COL).value = `Junction: ${j.label}`;
      ws.getCell(startRow, START_COL).font = { bold: true, size: 12 };

      // Header row
      let r = startRow + 2;
      ['From \\ To', ...arms, 'Total'].forEach((v, i) => {
        const c = ws.getCell(r, START_COL + i);
        c.value = v; c.border = border; c.alignment = centered; c.font = { bold: true }; c.fill = headerFill;
      });
      r++;
      const firstDataRow = r;

      for (let i = 0; i < n; i++) {
        const lc = ws.getCell(r, START_COL);
        lc.value = arms[i]; lc.border = border; lc.alignment = centered; lc.font = { bold: true }; lc.fill = headerFill;
        for (let k = 0; k < n; k++) {
          const c = ws.getCell(r, START_COL + 1 + k);
          c.border = border; c.alignment = centered;
          if (i === k) c.value = '—';
          else c.value = cellValue(j, arms[i], arms[k], `${colLetter(START_COL + 1 + k)}${r}`);
        }
        const tc = ws.getCell(r, START_COL + 1 + n);
        tc.border = border; tc.alignment = centered;
        tc.value = { formula: `SUM(${colLetter(START_COL + 1)}${r}:${colLetter(START_COL + n)}${r})` };
        r++;
      }

      // Totals row
      const tl = ws.getCell(r, START_COL);
      tl.value = 'Total'; tl.border = border; tl.alignment = centered; tl.font = { bold: true };
      for (let k = 0; k < n; k++) {
        const cl = colLetter(START_COL + 1 + k);
        const c = ws.getCell(r, START_COL + 1 + k);
        c.value = { formula: `SUM(${cl}${firstDataRow}:${cl}${r - 1})` };
        c.border = border; c.alignment = centered; c.font = { bold: true };
      }
      const gc = ws.getCell(r, START_COL + 1 + n);
      gc.value = { formula: `SUM(${colLetter(START_COL + 1)}${firstDataRow}:${colLetter(START_COL + n)}${r - 1})` };
      gc.border = border; gc.alignment = centered; gc.font = { bold: true };

      // Divider line beneath the block (diagram + matrix width).
      const dividerRow = startRow + BLOCK_ROWS - 2;
      const lastCol = START_COL + n + 1;
      for (let c = 1; c <= lastCol; c++) {
        ws.getCell(dividerRow, c).border = { bottom: { style: 'medium', color: { argb: 'FF7B4DBF' } } };
      }

      idx++;
    }

    ws.getColumn(START_COL).width = 16;
    for (let c = START_COL + 1; c <= START_COL + 10; c++) ws.getColumn(c).width = 10;
  }

  // One sheet per flow scenario, in order. Leaves (existing/development) write
  // numbers; growth/sum sheets use live formulas referencing the base/component
  // sheets at the same cell address (all sheets share an identical layout), so
  // editing an Existing figure flows through every derived scenario. Falls back
  // to Existing/Development/Existing+development if no scenarios are supplied.
  const scenarios = (flowScenarios && flowScenarios.length) ? flowScenarios : [
    { id: 'existing', name: 'Existing (survey)', kind: 'existing' },
    { id: 'development', name: 'Development', kind: 'development' },
  ];
  const used = new Set();
  const sheetOf = {};                         // flow-scenario id → worksheet name
  scenarios.forEach(fs => { sheetOf[fs.id] = sheetName(fs.name, used); });
  const ref = (id, addr) => `'${sheetOf[id]}'!${addr}`;

  scenarios.forEach(fs => {
    const ws = wb.addWorksheet(sheetOf[fs.id]);
    if (fs.kind === 'existing') {
      layoutSheet(ws, (j, from, to) => exAt(j.label, from, to));
    } else if (fs.kind === 'development') {
      layoutSheet(ws, (j, from, to) => devAt(j, from, to));
    } else if (fs.kind === 'growth') {
      const mult = growthMultById[fs.id] || 1;
      layoutSheet(ws, sheetOf[fs.baseId]
        ? (j, from, to, addr) => ({ formula: `ROUND(${ref(fs.baseId, addr)}*${mult},0)` })
        : () => 0);
    } else if (fs.kind === 'sum') {
      const parts = (fs.componentIds || []).filter(cid => sheetOf[cid]);
      layoutSheet(ws, parts.length
        ? (j, from, to, addr) => ({ formula: parts.map(cid => ref(cid, addr)).join('+') })
        : () => 0);
    } else {
      layoutSheet(ws, () => 0);
    }
  });

  return wb;
}

// Browser entry point: builds the workbook and triggers a download.
export async function exportJunctionScenarioExcel(opts) {
  if (!opts.junctionMatrices || opts.junctionMatrices.length === 0) return;
  const mod = await import('exceljs');
  const ExcelJS = mod.default || mod;
  const wb = buildJunctionWorkbook(ExcelJS, opts);
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(opts.projectName || 'project').replace(/\s+/g, '_')}_junction_matrices.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
